import { useState } from 'react'
import { Crown } from 'lucide-react'
import { cn } from '@/lib/utils'
import { seatStyle, turnBoard } from '../helpers'
import type { BoardProps, Cell, Pt } from './types'

interface Marked {
  cells: Cell[]
  turn: number
  line: Pt[] | null
  last: number | Pt | null
}

const onLine = (line: Pt[] | null, r: number, c: number) => (line ?? []).some(([lr, lc]) => lr === r && lc === c)

/** Whether it is this viewer's go, and so whether the board should take clicks. */
const mayPlay = (seat: number | null, turn: number, over: boolean, busy: boolean) => seat !== null && seat === turn && !over && !busy

/* ------------------------------------------------------------------ tic-tac-toe */

export function TicTacToe({ view, seat, over, busy, send }: BoardProps<Marked>) {
  const play = mayPlay(seat, view.turn, over, busy)
  return (
    <div className="mx-auto grid w-full max-w-[22rem] grid-cols-3 gap-2" role="grid" aria-label="Tic-tac-toe board">
      {view.cells.map((c, i) => {
        const mine = c !== null
        const win = onLine(view.line, Math.floor(i / 3), i % 3)
        return (
          <button
            key={i}
            type="button"
            disabled={!play || mine}
            onClick={() => send({ cell: i })}
            aria-label={`Square ${i + 1}${mine ? `, ${c === 0 ? 'X' : 'O'}` : ''}`}
            className={cn(
              'grid aspect-square place-items-center rounded-lg border border-border bg-card text-5xl font-bold transition-colors',
              play && !mine && 'hover:bg-accent',
              win && 'ring-2 ring-success',
              view.last === i && !win && 'bg-muted',
            )}
          >
            {mine ? <span className={seatStyle(c).text}>{c === 0 ? '✕' : '○'}</span> : null}
          </button>
        )
      })}
    </div>
  )
}

/* ------------------------------------------------------------------ connect four */

export function ConnectFour({ view, seat, over, busy, send }: BoardProps<Marked>) {
  const play = mayPlay(seat, view.turn, over, busy)
  const [hover, setHover] = useState<number | null>(null)
  const last = view.last as Pt | null
  return (
    <div className="mx-auto w-full max-w-md">
      <div className="mb-1 grid grid-cols-7 gap-1.5" aria-hidden>
        {Array.from({ length: 7 }, (_, c) => (
          <div key={c} className="grid h-6 place-items-center">
            {play && hover === c ? <span className={cn('size-4 rounded-full', seatStyle(seat ?? 0).bg)} /> : null}
          </div>
        ))}
      </div>
      <div className="grid grid-cols-7 gap-1.5 rounded-xl bg-brand-dark p-2" role="grid" aria-label="Connect four board">
        {view.cells.map((c, i) => {
          const r = Math.floor(i / 7)
          const col = i % 7
          const win = onLine(view.line, r, col)
          return (
            <button
              key={i}
              type="button"
              disabled={!play}
              onClick={() => send({ col })}
              onMouseEnter={() => setHover(col)}
              onMouseLeave={() => setHover(null)}
              onFocus={() => setHover(col)}
              aria-label={`Column ${col + 1}, row ${r + 1}${c !== null ? `, ${seatStyle(c).name}` : ', empty'}`}
              className={cn('aspect-square rounded-full transition-colors', c === null ? 'bg-background' : seatStyle(c).bg, win && 'ring-4 ring-success', last && last[0] === r && last[1] === col && !win && 'ring-2 ring-background')}
            />
          )
        })}
      </div>
    </div>
  )
}

/* ------------------------------------------------------------------ gomoku */

export function Gomoku({ view, seat, over, busy, send }: BoardProps<Marked>) {
  const play = mayPlay(seat, view.turn, over, busy)
  const last = view.last as Pt | null
  const N = 13
  return (
    <div className="mx-auto w-full max-w-xl rounded-xl border border-border bg-warning-soft p-2">
      <div className="grid" style={{ gridTemplateColumns: `repeat(${N}, minmax(0, 1fr))` }} role="grid" aria-label="Gomoku board">
        {view.cells.map((c, i) => {
          const r = Math.floor(i / N)
          const col = i % N
          const win = onLine(view.line, r, col)
          return (
            <button
              key={i}
              type="button"
              disabled={!play || c !== null}
              onClick={() => send({ r, c: col })}
              aria-label={`Row ${r + 1}, column ${col + 1}${c !== null ? `, ${seatStyle(c).name}` : ''}`}
              className="group relative aspect-square"
            >
              {/* The lines of the board run through the middle of each point. */}
              <span className={cn('absolute left-1/2 w-px -translate-x-1/2 bg-border-strong', r === 0 ? 'top-1/2 bottom-0' : r === N - 1 ? 'top-0 bottom-1/2' : 'inset-y-0')} />
              <span className={cn('absolute top-1/2 h-px -translate-y-1/2 bg-border-strong', col === 0 ? 'left-1/2 right-0' : col === N - 1 ? 'left-0 right-1/2' : 'inset-x-0')} />
              {c !== null ? (
                <span className={cn('absolute inset-[12%] rounded-full shadow-sm', seatStyle(c).bg, win && 'ring-2 ring-success', last && last[0] === r && last[1] === col && !win && 'ring-2 ring-background')} />
              ) : play ? (
                <span className={cn('absolute inset-[30%] rounded-full opacity-0 group-hover:opacity-40', seatStyle(seat ?? 0).bg)} />
              ) : null}
            </button>
          )
        })}
      </div>
    </div>
  )
}

/* ------------------------------------------------------------------ reversi */

interface ReversiView {
  cells: Cell[]
  turn: number
  legal: Pt[]
  counts: [number, number]
  passed: number | null
  last: Pt | null
}

export function Reversi({ view, seat, over, busy, send, players, people }: BoardProps<ReversiView>) {
  const play = mayPlay(seat, view.turn, over, busy)
  const legal = new Set(view.legal.map(([r, c]) => r * 8 + c))
  const passedName = view.passed !== null ? (people.get(players[view.passed] ?? '')?.displayName.split(' ')[0] ?? 'Someone') : null
  return (
    <div className="mx-auto w-full max-w-md">
      <div className="grid grid-cols-8 gap-px rounded-lg border border-border bg-border-strong p-px" role="grid" aria-label="Reversi board">
        {view.cells.map((c, i) => {
          const r = Math.floor(i / 8)
          const col = i % 8
          const can = play && legal.has(i)
          return (
            <button
              key={i}
              type="button"
              disabled={!can}
              onClick={() => send({ r, c: col })}
              aria-label={`Row ${r + 1}, column ${col + 1}${c !== null ? `, ${seatStyle(c).name}` : can ? ', you can play here' : ''}`}
              className={cn('relative grid aspect-square place-items-center bg-success-soft', can && 'hover:bg-accent')}
            >
              {c !== null ? (
                <span className={cn('size-[78%] rounded-full shadow-sm', seatStyle(c).bg, view.last && view.last[0] === r && view.last[1] === col && 'ring-2 ring-foreground')} />
              ) : can ? (
                <span className={cn('size-[22%] rounded-full opacity-60', seatStyle(seat ?? 0).bg)} />
              ) : null}
            </button>
          )
        })}
      </div>
      <p className="mt-2 flex items-center justify-center gap-4 text-sm">
        {[0, 1].map((s) => (
          <span key={s} className="flex items-center gap-1.5 font-medium">
            <span className={cn('size-3.5 rounded-full', seatStyle(s).bg)} /> <span className="num tabular-nums">{view.counts[s]}</span>
          </span>
        ))}
      </p>
      {passedName && !over ? <p className="mt-1 text-center text-xs text-text-muted">{passedName} had no move and was skipped.</p> : null}
    </div>
  )
}

/* ------------------------------------------------------------------ checkers */

interface Move {
  from: Pt
  to: Pt
  over: Pt | null
}
interface CheckersView {
  board: number[]
  turn: number
  must: Pt | null
  last: Move | null
  legal: Move[]
}

const ownerOf = (v: number) => (v === 0 ? -1 : v <= 2 ? 0 : 1)

export function Checkers({ view, seat, over, busy, send }: BoardProps<CheckersView>) {
  const play = mayPlay(seat, view.turn, over, busy)
  const [picked, setPicked] = useState<Pt | null>(null)
  // The player at the top sees the board turned round, so their own pieces are always nearest.
  const flip = seat === 1
  const from = view.must ?? picked
  const targets = from ? view.legal.filter((m) => m.from[0] === from[0] && m.from[1] === from[1]) : []
  const movable = new Set(view.legal.map((m) => m.from[0] * 8 + m.from[1]))

  const click = (r: number, c: number) => {
    if (!play) return
    const target = targets.find((m) => m.to[0] === r && m.to[1] === c)
    if (target) {
      setPicked(null)
      send({ from: target.from, to: target.to })
    } else if (!view.must && movable.has(r * 8 + c)) setPicked([r, c])
    else if (!view.must) setPicked(null)
  }

  return (
    <div className="mx-auto w-full max-w-md">
      <div className="grid grid-cols-8 overflow-hidden rounded-lg border border-border-strong" role="grid" aria-label="Checkers board">
        {Array.from({ length: 64 }, (_, k) => {
          const [r, c] = turnBoard(Math.floor(k / 8), k % 8, 8, flip)
          const v = view.board[r * 8 + c]!
          const dark = (r + c) % 2 === 1
          const dest = targets.some((m) => m.to[0] === r && m.to[1] === c)
          const isFrom = from !== null && from[0] === r && from[1] === c
          const o = ownerOf(v)
          const lastMove = view.last && ((view.last.from[0] === r && view.last.from[1] === c) || (view.last.to[0] === r && view.last.to[1] === c))
          return (
            <button
              key={k}
              type="button"
              disabled={!play || !dark || (!dest && !movable.has(r * 8 + c))}
              onClick={() => click(r, c)}
              aria-label={`Row ${r + 1}, column ${c + 1}${o >= 0 ? `, ${seatStyle(o).name} ${v === 2 || v === 4 ? 'king' : 'piece'}` : ''}${dest ? ', you can move here' : ''}`}
              className={cn('relative grid aspect-square place-items-center', dark ? 'bg-text-muted/40' : 'bg-card', lastMove && dark && 'bg-warning-soft', dest && 'bg-success-soft ring-2 ring-inset ring-success')}
            >
              {o >= 0 ? (
                <span className={cn('grid size-[78%] place-items-center rounded-full border-2 border-background shadow-sm', seatStyle(o).bg, isFrom && 'ring-2 ring-foreground')}>
                  {v === 2 || v === 4 ? <Crown className="size-[55%] text-text-inverse" aria-hidden /> : null}
                </span>
              ) : dest ? (
                <span className="size-[22%] rounded-full bg-success opacity-70" />
              ) : null}
            </button>
          )
        })}
      </div>
      {view.must && play ? <p className="mt-2 text-center text-xs font-medium text-brand">Keep jumping with the same piece.</p> : null}
      {!view.must && play && view.legal.some((m) => m.over) ? <p className="mt-2 text-center text-xs font-medium text-brand">You have a capture to make.</p> : null}
    </div>
  )
}
